import React from 'react';
import { Card, CardBody, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import './SuperAdminDashboardStats.css';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: string;
  color: 'primary' | 'success' | 'warning' | 'danger' | 'info';
  linkTo?: string;
  showArrow?: boolean;
  isLastInRow?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ 
  title, 
  value, 
  icon, 
  color, 
  linkTo, 
  showArrow = false, 
  isLastInRow = false 
}) => {
  const navigate = useNavigate();

  const handleCardClick = () => {
    if (linkTo) {
      navigate(linkTo);
    }
  };

  // Format value based on title
  const formatValue = (val: string | number, title: string) => {
    if (typeof val === 'number') {
      if (title.toLowerCase().includes('amount') || title.toLowerCase().includes('dues')) {
        return `₹${val.toLocaleString()}`;
      }
      return val.toLocaleString();
    }
    return val;
  };

  return (
    <div className="stat-card-wrapper">
      <Card 
        className={`stat-card h-100 border-0 shadow-sm ${linkTo ? 'clickable-card' : ''}`}
        onClick={handleCardClick}
      >
        <CardBody className="p-3">
          <div className="d-flex align-items-center">
            {/* Icon on the left */}
            <div className={`stat-icon bg-${color}-subtle rounded d-flex align-items-center justify-content-center me-3`}>
              <i className={`${icon} text-${color}`} style={{ fontSize: '1.25rem' }}></i>
            </div>
            
            {/* Text content on the right */}
            <div className="flex-grow-1">
              <h4 className={`mb-1 fw-bold text-${color}`}>{formatValue(value, title)}</h4>
              <h6 className="text-muted mb-0 small">{title}</h6>
            </div>
            
            {/* Arrow on the far right */}
            {showArrow && !isLastInRow && (
              <div className="connecting-arrow">
                <i className="ph-arrow-right text-muted"></i>
              </div>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

interface SuperAdminDashboardStatsProps {
  stats: {
    totalAmount?: number;
    totalDues?: number;
    totalPlayers?: number;
    approvedPlayers?: number;
    rejectedPlayers?: number;
    // Keep existing fields for backward compatibility
    teams?: number;
    players?: number;
    tournaments?: number;
    payments?: number;
    activePlayers?: number;
    inactivePlayers?: number;
    activeTeams?: number;
    inactiveTeams?: number;
  };
}

const SuperAdminDashboardStats: React.FC<SuperAdminDashboardStatsProps> = ({ stats }) => {
  // Super Admin specific cards with new API fields - matching Club Manager styling
  const firstRowCards = [
    {
      title: "Total Amount",
      value: stats.totalAmount || stats.payments || 0,
      icon: "ph-currency-inr",
      color: "success" as const,
      linkTo: "/super-admin-payments"
    },
    {
      title: "Total Dues",
      value: stats.totalDues || 0,
      icon: "ph-warning-circle",
      color: "warning" as const,
      linkTo: "/super-admin-payments"
    },
    {
      title: "Total Players",
      value: stats.totalPlayers || stats.players || 0,
      icon: "ph-users-three",
      color: "primary" as const,
      linkTo: "/manage-players"
    },
    {
      title: "Approved Players",
      value: stats.approvedPlayers || stats.activePlayers || 0,
      icon: "ph-check-circle",
      color: "success" as const,
      linkTo: "/manage-players"
    }
  ];

  const secondRowCards = [
    {
      title: "Rejected Players",
      value: stats.rejectedPlayers || stats.inactivePlayers || 0,
      icon: "ph-x-circle",
      color: "danger" as const,
      linkTo: "/manage-players"
    }
  ];

  return (
    <div className="dashboard-stats-container mb-4">
      {/* First Row - 4 Cards */}
      <Row className="mb-3">
        {firstRowCards.map((stat, index) => (
          <Col lg={3} md={6} sm={6} key={index} className="mb-3">
            <StatCard 
              {...stat} 
              showArrow={index < firstRowCards.length - 1}
              isLastInRow={index === firstRowCards.length - 1}
            />
          </Col>
        ))}
      </Row>

      {/* Second Row - 1 Card (Left Aligned) */}
      <Row>
        <Col lg={3} md={6} sm={6} className="mb-3">
          <StatCard 
            {...secondRowCards[0]} 
            showArrow={false}
            isLastInRow={true}
          />
        </Col>
      </Row>
    </div>
  );
};

export default SuperAdminDashboardStats; 